import { apiClient } from '@/lib/axios';
import type { Lead, LeadDetail, LeadRemark, PaginatedResponse, ApiResponse } from '@/types';

export interface LeadFilters {
  page?: number;
  limit?: number;
  status?: string;
  source?: string;
  category?: string;
  city?: string;
  assignedToId?: string;
  assignedToName?: string;
  disease?: string;
  dateFrom?: string;
  dateTo?: string;
  dueDateFrom?: string;
  dueDateTo?: string;
  search?: string;
  nameSearch?: string;
  phoneSearch?: string;
  emailSearch?: string;
  tags?: string[];
  opdDone?: boolean;
  ipdDone?: boolean;
  crossCategoryOnly?: boolean;
  isInsuranceCase?: boolean;
  isEmiCase?: boolean;
}

export interface CreateLeadInput {
  name: string;
  phone: string;
  email?: string;
  age?: string;
  disease?: string;
  treatment?: string;
  location?: string;
  city?: string;
  category?: string;
  source?: string;
  tags?: string[];
  dueDate?: string;
}

export const leadsApi = {
  getAll: async (filters: LeadFilters = {}): Promise<PaginatedResponse<Lead>> => {
    const { data } = await apiClient.get<ApiResponse<PaginatedResponse<Lead>>>('/leads', { params: filters });
    return data.data!;
  },

  getById: async (id: string): Promise<LeadDetail> => {
    const { data } = await apiClient.get<ApiResponse<{ lead: LeadDetail }>>(`/leads/${id}`);
    return data.data!.lead;
  },

  create: async (input: CreateLeadInput): Promise<Lead> => {
    const { data } = await apiClient.post<ApiResponse<{ lead: Lead }>>('/leads', input);
    return data.data!.lead;
  },

  update: async (id: string, input: Partial<CreateLeadInput & { status: string; opdDone: boolean; ipdDone: boolean; doctorId: string; hospitalId: string; isInsuranceCase: boolean; isEmiCase: boolean }>): Promise<Lead> => {
    const { data } = await apiClient.patch<ApiResponse<{ lead: Lead }>>(`/leads/${id}`, input);
    return data.data!.lead;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/leads/${id}`);
  },

  addRemark: async (id: string, remark: string, type = 'general'): Promise<void> => {
    await apiClient.post(`/leads/${id}/remarks`, { remark, type });
  },

  getActivity: async (id: string, page = 1, limit = 15): Promise<PaginatedResponse<LeadRemark>> => {
    const { data } = await apiClient.get<ApiResponse<PaginatedResponse<LeadRemark>>>(`/leads/${id}/activity`, { params: { page, limit } });
    return data.data!;
  },

  assign: async (id: string, toUserId: string, assignmentType = 'manual', reason?: string): Promise<Lead> => {
    const { data } = await apiClient.post<ApiResponse<{ lead: Lead }>>(`/leads/${id}/assign`, { toUserId, assignmentType, reason });
    return data.data!.lead;
  },

  autoAssign: async (id: string, category?: string): Promise<Lead> => {
    const { data } = await apiClient.post<ApiResponse<{ lead: Lead }>>(`/leads/${id}/assign/auto`, { category });
    return data.data!.lead;
  },

  getDashboardStats: async (): Promise<{
    total: number; new_leads: number; followup: number;
    opd_scheduled: number; opd_done: number; ipd_scheduled: number; ipd_done: number;
    insurance: number; preauth: number; emi: number; closed_won: number; closed_lost: number; not_interested: number;
    sourceStats: { source: string | null; count: number }[];
  }> => {
    const { data } = await apiClient.get<ApiResponse<unknown>>('/leads/stats/dashboard');
    return data.data as ReturnType<typeof leadsApi.getDashboardStats> extends Promise<infer T> ? T : never;
  },
};
